// src/contexts/AuthContext.tsx - Version finale avec Super Admin

import React, { createContext, useContext, useEffect, useRef, useState } from 'react';
import { 
  User, 
  signInWithEmailAndPassword, 
  signOut as firebaseSignOut,
  onAuthStateChanged,
} from 'firebase/auth';
import { doc, getDoc, setDoc, updateDoc, collection, query, where, getDocs, deleteField } from 'firebase/firestore';
import { useQueryClient } from '@tanstack/react-query';
import { auth, db } from '../config/firebase';
import { UserProfile } from '../types/user';
import { Business, DEFAULT_BUSINESS_CONFIGURATION } from '../types/business';
import { COLLECTIONS } from '../config/collections';
import { isAppLocale } from '../i18n/types';
import {
  clearUsageSessionFlags,
  flushUsageTracking,
  hasMarkedSessionStart,
  markSessionStart,
  setUsageTrackingContext,
  trackUsage,
} from '../utils/usageTracking';
import { sanitizeUserPushPrefs } from '../utils/pushPrefs';

interface AuthContextType {
  user: User | null;
  userProfile: UserProfile | null;
  currentBusiness: Business | null;
  loading: boolean;
  signIn: (email: string, password: string) => Promise<void>;
  signOut: () => Promise<void>;
  updateLastLogin: () => Promise<void>;
  // employeeId supprimé - Utiliser employeeIds[businessId] à la place
  employeeName: string | null;

  // Nouvelles propriétés Super Admin / CSM
  isSuperAdmin: boolean;
  isCSM: boolean;  // Customer Success Manager (accès admin avec restrictions)
  switchToBusiness: (businessId: string) => Promise<void>;
  switchToSuperAdmin: () => void;
  
  // Nouvelles propriétés Multi-Business
  needsBusinessSelection: boolean;
  availableBusinesses: Business[];
  selectBusiness: (businessId: string) => Promise<void>;
  switchBusiness: () => void;
  /** Recharge le business courant depuis Firestore (ex. après maj branding / facturation). */
  refreshCurrentBusiness: () => Promise<void>;
  /** Mise à jour partielle optimiste du profil (ex. preferredLocale). */
  patchUserProfile: (patch: Partial<UserProfile>) => void;
}

export const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const queryClient = useQueryClient();
  const [user, setUser] = useState<User | null>(null);
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null);
  const [currentBusiness, setCurrentBusiness] = useState<Business | null>(null);
  const [loading, setLoading] = useState(true);
  const [employeeName, setEmployeeName] = useState<string | null>(null);
  const [availableBusinesses, setAvailableBusinesses] = useState<Business[]>([]);
  const pendingExplicitLoginRef = useRef(false);

  // Propriétés calculées
  const isSuperAdmin = userProfile?.isSuperAdmin === true;
  const isCSM = userProfile?.isCSM === true;
  // ✅ MODIFIÉ : Utiliser employeeIds[businessId] au lieu de employeeId
  const employeeId = currentBusiness?.id && userProfile?.employeeIds?.[currentBusiness.id] || null;
  
  // ✅ NOUVEAU COMPORTEMENT :
  // - Utilisateurs "normaux" (ni Super Admin ni CSM) :
  //   - S'il y a PLUSIEURS business et aucun currentBusiness sélectionné → page de sélection des business
  //   - S'il n'y a QU'UN business → sélection automatique (gérée plus bas dans onAuthStateChanged), donc pas de sélection manuelle
  // - Super Admin / CSM : contrôlés séparément dans AppRoutes (ne passent pas par needsBusinessSelection)
  const userBusinessCount = userProfile?.businessIds?.length ?? 0;
  const needsBusinessSelection = Boolean(
    userProfile &&
    !isSuperAdmin &&
    !isCSM &&
    !currentBusiness &&
    userBusinessCount > 1
  );
  

  const loadEmployeeInfo = async (userProfile: UserProfile, businessId: string): Promise<string | null> => {
    try {
      const employeeId = userProfile.employeeIds?.[businessId];
      if (!employeeId) return null;

      const employeeDoc = await getDoc(doc(db, COLLECTIONS.employees, employeeId));
      if (employeeDoc.exists()) {
        const employeeData = employeeDoc.data();
        // Vérifier que l'employé appartient au bon business et est actif
        if (employeeData.businessId === businessId && employeeData.isActive === true) {
          return employeeData.name;
        }
      }
      return null;
    } catch (error) {
      console.error('Erreur lors du chargement des informations employé:', error);
      return null;
    }
  };

  // Fonction pour charger le business avec configuration
  const loadBusiness = async (businessId: string): Promise<Business | null> => {
    try {
      const businessDoc = await getDoc(doc(db, COLLECTIONS.businesses, businessId));
      if (businessDoc.exists()) {
        const data = businessDoc.data();
        
        // Récupérer ou créer la configuration par défaut
        let configuration = data.configuration;
        
        // Si pas de configuration, utiliser les valeurs par défaut
        if (!configuration) {
          configuration = DEFAULT_BUSINESS_CONFIGURATION;
          
          // Sauvegarder la configuration par défaut dans Firestore
          try {
            await updateDoc(businessDoc.ref, { configuration });
            console.log(`Configuration par défaut sauvegardée pour business ${businessId}`);
          } catch (configError) {
            console.warn('Erreur sauvegarde configuration:', configError);
          }
        }
        
        const rawProgram = data.loyaltyPassProgram;
        const loyaltyPassProgram = rawProgram
          ? {
              ...rawProgram,
              updatedAt:
                typeof rawProgram.updatedAt?.toDate === 'function'
                  ? rawProgram.updatedAt.toDate()
                  : rawProgram.updatedAt,
            }
          : undefined;

        return {
          id: businessDoc.id,
          name: data.name,
          type: data.type || 'beauty_salon',
          isActive: data.isActive,
          configurationInProgress: data.configurationInProgress ?? false,
          createdAt: data.createdAt?.toDate(),
          configuration,
          branding: data.branding,
          activeFeatures: data.activeFeatures,
          disabledFeatures: data.disabledFeatures,
          ...(typeof data.bonspotMerchantId === 'string'
            ? { bonspotMerchantId: data.bonspotMerchantId }
            : {}),
          ...(loyaltyPassProgram ? { loyaltyPassProgram } : {}),
        } as Business;
      }
      return null;
    } catch (error) {
      console.error('Erreur lors du chargement du business:', error);
      return null;
    }
  };

  // Charger le profil utilisateur avec support multi-business
  const loadUserProfile = async (user: User): Promise<UserProfile> => {
    const userDoc = await getDoc(doc(db, COLLECTIONS.users, user.uid));
    
    if (!userDoc.exists()) {
      throw new Error('UTILISATEUR_NON_AUTORISE');
    }
    
    const userData = userDoc.data();
    
    console.log('📊 Données brutes Firestore:', userData);
    
    // Migration automatique de l'ancien format vers le nouveau
    let businessIds: string[] = [];
    let currentBusinessId: string | undefined = undefined;
    
    if (userData.businessIds && Array.isArray(userData.businessIds)) {
      // Nouveau format
      console.log('✅ Format nouveau détecté');
      businessIds = userData.businessIds;
      currentBusinessId = userData.currentBusinessId;
    } else if (userData.businessId) {
      // Ancien format - migration automatique
      businessIds = [userData.businessId];
      currentBusinessId = userData.businessId;
      
      // Mettre à jour le document avec le nouveau format
      try {
        await updateDoc(userDoc.ref, {
          businessIds: businessIds,
          currentBusinessId: currentBusinessId,
        });
        console.log('Migration automatique effectuée pour:', user.uid);
      } catch (error) {
        console.warn('Erreur migration automatique:', error);
      }
    }
    
    // Nettoyer les anciens champs si présents
    if (userData.businessId && userData.businessIds) {
      try {
        await updateDoc(userDoc.ref, {
          businessId: deleteField(),
        });
        console.log('Ancien champ businessId supprimé pour:', user.uid);
      } catch (error) {
        console.warn('Erreur suppression ancien champ:', error);
      }
    }
    
    // Migration depuis l'ancien format (role) vers le nouveau (roleProfileIds + isSuperAdmin)
    const isSuperAdmin = userData.role === 'super_admin' || userData.isSuperAdmin === true;
    const isCSM = userData.role === 'csm' || userData.isCSM === true;

    // Construire roleProfileIds depuis les données
    let roleProfileIds: { [businessId: string]: string } | undefined = undefined;
    if (userData.roleProfileIds && typeof userData.roleProfileIds === 'object') {
      roleProfileIds = userData.roleProfileIds;
    }

    const profile: UserProfile = {
      id: user.uid,
      email: userData.email,
      name: userData.name,
      role: userData.role || undefined, // ✅ Rôle métier (optionnel)
      roleProfileIds, // Profils par business
      isSuperAdmin,  // Flag SuperAdmin
      isCSM,  // Customer Success Manager
      businessIds,
      currentBusinessId,
      employeeIds: userData.employeeIds || {}, // ✅ NOUVEAU : employeeIds par business
      preferredLocale: isAppLocale(userData.preferredLocale)
        ? userData.preferredLocale
        : undefined,
      seenTours:
        userData.seenTours && typeof userData.seenTours === 'object'
          ? (userData.seenTours as UserProfile['seenTours'])
          : undefined,
      pushPrefs: sanitizeUserPushPrefs(userData.pushPrefs),
      isActive: userData.isActive,
      createdAt: userData.createdAt?.toDate() || new Date(),
      lastLogin: userData.lastLogin?.toDate(),
    };

    console.log('Profil chargé:', profile);
    console.log('businessIds:', businessIds);
    console.log('currentBusinessId:', currentBusinessId);

    // Super Admin n'a pas besoin de businessIds ; CSM doit en avoir
    if (!profile.isSuperAdmin && !profile.isCSM && businessIds.length === 0) {
      console.warn('Utilisateur sans businessIds détecté:', user.uid);
      console.warn('businessIds trouvés:', businessIds);
      throw new Error('UTILISATEUR_SANS_BUSINESS');
    }

    return profile;
  };

  // Charger tous les business accessibles pour un utilisateur
  const loadAvailableBusinesses = async (businessIds: string[]): Promise<Business[]> => {
    const businesses: Business[] = [];
    
    for (const businessId of businessIds) {
      try {
        const business = await loadBusiness(businessId);
        if (business && business.isActive) {
          businesses.push(business);
        }
      } catch (error) {
        console.warn(`Erreur chargement business ${businessId}:`, error);
      }
    }
    
    return businesses;
  };

  // Sélectionner un business (pour utilisateurs multi-business)
  const selectBusiness = async (businessId: string) => {
    if (!userProfile || !userProfile.businessIds.includes(businessId)) {
      throw new Error('Business non autorisé');
    }

    try {
      const business = await loadBusiness(businessId);
      if (!business || !business.isActive) {
        throw new Error('Business introuvable ou inactif');
      }

      // Mettre à jour currentBusinessId dans Firestore
      if (user) {
        await updateDoc(doc(db, COLLECTIONS.users, user.uid), {
          currentBusinessId: businessId,
        });
      }

      // Mettre à jour le profil local
      setUserProfile(prev => prev ? {
        ...prev,
        currentBusinessId: businessId
      } : null);

      setCurrentBusiness(business);
      
      // Charger les informations employé si disponibles
      if (userProfile) {
        const empName = await loadEmployeeInfo(userProfile, businessId);
        setEmployeeName(empName);
      }
      
      // Invalider toutes les queries React Query pour forcer le rechargement des données
      // Cela garantit que les permissions et autres données sont rechargées pour le nouveau business
      queryClient.invalidateQueries();
      
      console.log('Business sélectionné:', business.name);
      
    } catch (error) {
      console.error('Erreur lors de la sélection du business:', error);
      throw error;
    }
  };

  // Changer de business (retour à l'écran de sélection)
  const switchBusiness = () => {
    if (!userProfile || userProfile.businessIds.length <= 1) {
      return;
    }
    
    setCurrentBusiness(null);
    setEmployeeName(null);
    
    // Mettre à jour currentBusinessId à null
    if (user) {
      updateDoc(doc(db, COLLECTIONS.users, user.uid), {
        currentBusinessId: null,
      }).catch(console.error);
      
      setUserProfile(prev => prev ? {
        ...prev,
        currentBusinessId: undefined
      } : null);
    }
    
    console.log('Retour à la sélection de business');
  };

  // Super Admin / CSM peuvent "impersonner" un business (entrer dans l'interface client)
  const switchToBusiness = async (businessId: string) => {
    if (!isSuperAdmin && !isCSM) {
      throw new Error('Seuls les super admins ou CSM peuvent changer de business');
    }

    try {
      const business = await loadBusiness(businessId);
      if (!business) {
        throw new Error('Business introuvable');
      }

      // Mettre à jour le currentBusiness et le currentBusinessId
      setCurrentBusiness(business);
      
      // Mettre à jour le profil utilisateur avec le currentBusinessId
      if (userProfile) {
        setUserProfile({
          ...userProfile,
          currentBusinessId: businessId
        });
      }
      
      console.log('Super Admin connecté au business:', business.name);
      
      // Note: La redirection se fera automatiquement via le re-render de App.tsx
      // car userProfile.currentBusinessId a été mis à jour
      
    } catch (error) {
      console.error('Erreur lors du changement de business:', error);
      throw error;
    }
  };

  // Revenir à l'espace admin (Super Admin / CSM)
  const switchToSuperAdmin = () => {
    if (!isSuperAdmin && !isCSM) {
      throw new Error('Seuls les super admins ou CSM peuvent utiliser cette fonction');
    }

    setCurrentBusiness(null);
    setEmployeeName(null);
    
      // Réinitialiser le currentBusinessId dans le profil
      if (userProfile) {
        setUserProfile({
          ...userProfile,
          currentBusinessId: undefined
        });
      }
    
    console.log('Retour au mode Super Admin pur');
    
    // Note: La redirection se fera automatiquement via le re-render de App.tsx
    // car userProfile.currentBusinessId a été mis à undefined
  };

  const refreshCurrentBusiness = async () => {
    if (!currentBusiness?.id) return;
    const business = await loadBusiness(currentBusiness.id);
    if (!business) return;
    setCurrentBusiness(business);
    setAvailableBusinesses((prev) =>
      prev.map((b) => (b.id === business.id ? business : b))
    );
  };

  const patchUserProfile = (patch: Partial<UserProfile>) => {
    setUserProfile((prev) => (prev ? { ...prev, ...patch } : null));
  };

  // Mettre à jour la dernière connexion
  const updateLastLogin = async () => {
    if (user && userProfile) {
      try {
        const userDocRef = doc(db, COLLECTIONS.users, user.uid);
        await updateDoc(userDocRef, {
          lastLogin: new Date(),
        });
        
        setUserProfile(prev => prev ? {
          ...prev,
          lastLogin: new Date()
        } : null);
        
      } catch (error) {
        console.error('Erreur lors de la mise à jour de lastLogin:', error);
      }
    }
  };

  // Connexion
  const signIn = async (email: string, password: string) => {
    try {
      await signInWithEmailAndPassword(auth, email, password);
      pendingExplicitLoginRef.current = true;
    } catch (error: any) {
      pendingExplicitLoginRef.current = false;
      console.error('Erreur de connexion Firebase Auth:', error);
      
      let errorMessage = 'Erreur de connexion';
      if (error.code === 'auth/user-not-found') {
        errorMessage = 'Utilisateur non trouvé';
      } else if (error.code === 'auth/wrong-password') {
        errorMessage = 'Mot de passe incorrect';
      } else if (error.code === 'auth/invalid-email') {
        errorMessage = 'Format d\'email invalide';
      } else if (error.code === 'auth/user-disabled') {
        errorMessage = 'Compte désactivé';
      } else if (error.code === 'auth/invalid-credential') {
        errorMessage = 'Identifiants invalides';
      } else if (error.code === 'auth/too-many-requests') {
        errorMessage = 'Trop de tentatives de connexion. Réessayez plus tard.';
      } else if (error.code === 'auth/network-request-failed') {
        errorMessage = 'Erreur réseau. Vérifiez votre connexion internet.';
      } else if (error.code === 'auth/api-key-not-valid') {
        errorMessage = 'Clé API Firebase invalide. Vérifiez REACT_APP_FIREBASE_API_KEY dans .env.development (copiez depuis la console Firebase) et que http://localhost:3000 est autorisé dans Google Cloud Console → Credentials → restrictions HTTP. Redémarrez le serveur (npm start) après modification.';
      }
      
      throw new Error(errorMessage);
    }
  };

  // Déconnexion forcée avec nettoyage complet
  const signOut = async () => {
    try {
      trackUsage('logout');
      await flushUsageTracking();
      clearUsageSessionFlags();
      pendingExplicitLoginRef.current = false;
      setUsageTrackingContext({
        businessId: null,
        userId: null,
        role: null,
        suppress: true,
      });

      const uid = auth.currentUser?.uid;
      if (uid) {
        try {
          const { disableCurrentPushDevice, deleteLocalFcmToken } = await import(
            '../services/pushDeviceService'
          );
          await disableCurrentPushDevice(uid);
          await deleteLocalFcmToken();
        } catch {
          // best-effort — ne bloque pas le logout
        }
      }

      await firebaseSignOut(auth);
      setUser(null);
      setUserProfile(null);
      setCurrentBusiness(null);
      setEmployeeName(null);
      setAvailableBusinesses([]);
      
      // Nettoyer le localStorage si nécessaire
      localStorage.clear();
      sessionStorage.clear();
      
    } catch (error) {
      console.error('Erreur lors de la déconnexion:', error);
      
      // Forcer la déconnexion côté client même si Firebase échoue
      setUser(null);
      setUserProfile(null);
      setCurrentBusiness(null);
      setEmployeeName(null);
      setAvailableBusinesses([]);
      localStorage.clear();
      sessionStorage.clear();
      
      // Recharger la page pour s'assurer du nettoyage complet
      window.location.reload();
    }
  };

  // Sync contexte tracking + login / session_start
  useEffect(() => {
    const consoleOnly =
      !!(userProfile?.isSuperAdmin || userProfile?.isCSM) && !currentBusiness;

    setUsageTrackingContext({
      userId: user?.uid ?? null,
      businessId: currentBusiness?.id ?? null,
      role: userProfile?.isSuperAdmin
        ? 'super_admin'
        : userProfile?.isCSM
          ? 'csm'
          : userProfile?.role ?? null,
      suppress: consoleOnly || !user,
    });

    if (user && currentBusiness && !consoleOnly) {
      if (pendingExplicitLoginRef.current) {
        pendingExplicitLoginRef.current = false;
        trackUsage('login');
        markSessionStart();
      } else if (!hasMarkedSessionStart()) {
        markSessionStart();
        trackUsage('session_start');
      }
    }
  }, [user, userProfile, currentBusiness]);

  // Flush best-effort quand l'onglet passe en arrière-plan
  useEffect(() => {
    const onVisibility = () => {
      if (document.visibilityState === 'hidden') {
        void flushUsageTracking();
      }
    };
    window.addEventListener('visibilitychange', onVisibility);
    return () => window.removeEventListener('visibilitychange', onVisibility);
  }, []);

  // Écouter les changements d'authentification avec validation stricte
// Écouter les changements d'authentification avec validation stricte
useEffect(() => {
  const unsubscribe = onAuthStateChanged(auth, async (user) => {
    setLoading(true);
    
    try {
      if (user) {
        console.log('Utilisateur connecté, chargement du profil...');
        
        try {
          // Charger le profil utilisateur avec validation stricte
          const profile = await loadUserProfile(user);
          
          // Vérifier que l'utilisateur est actif
          if (!profile.isActive) {
            console.error('Utilisateur inactif:', profile.id);
            await signOut();
            return;
          }
          
          setUser(user);
          setUserProfile(profile);
          
          // Super Admin / CSM : gestion spécifique
          if (profile.isSuperAdmin || profile.isCSM) {
            const businessIds = profile.businessIds || [];
            console.log(profile.isSuperAdmin ? 'Super Admin' : 'CSM', 'connecté:', profile.name);

            if (profile.isSuperAdmin) {
              // Super Admin : on charge simplement la liste des businesses accessibles
              setCurrentBusiness(null);
              setEmployeeName(null);
              setAvailableBusinesses(businessIds.length > 0 ? await loadAvailableBusinesses(businessIds) : []);
            } else {
              // CSM : toujours passer par la sélection de business
              setCurrentBusiness(null);
              setEmployeeName(null);
              setAvailableBusinesses(businessIds.length > 0 ? await loadAvailableBusinesses(businessIds) : []);
            }

            await updateLastLogin();
          } else {
            // Utilisateurs "normaux" (ni Super Admin ni CSM) : gérer les business accessibles
            if (profile.businessIds.length === 0) {
              console.error('Utilisateur admin/manager sans businessIds:', profile.id);
              throw new Error('UTILISATEUR_SANS_BUSINESS');
            }
            
            // Charger tous les business accessibles (actifs)
            const businesses = await loadAvailableBusinesses(profile.businessIds);
            setAvailableBusinesses(businesses);
            
            if (businesses.length === 0) {
              console.error('Aucun business actif trouvé pour l\'utilisateur:', profile.id);
              throw new Error('AUCUN_BUSINESS_ACTIF');
            }
            
            // Filtrer les business connectables (non en configuration)
            const connectableBusinesses = businesses.filter(b => !b.configurationInProgress);
            const connectableCount = connectableBusinesses.length;

            if (connectableCount === 0) {
              console.error('Tous les business de l\'utilisateur sont en configuration:', profile.id);
              throw new Error('AUCUN_BUSINESS_CONNECTABLE');
            }

            const hasConfigInProgress = businesses.some(b => b.configurationInProgress);

            // 🔹 Cas 1 : plusieurs business au total ET au moins un en configuration
            //           → toujours afficher la page de sélection pour que l'utilisateur voie le business bloqué
            if (businesses.length > 1 && hasConfigInProgress) {
              console.log('Plusieurs business dont au moins un en configuration, sélection requise');
              setCurrentBusiness(null);
              setEmployeeName(null);
            } else if (connectableCount === 1) {
              // 🔹 Cas 2 : un seul business connectable et aucun autre en configuration → sélection automatique
              const onlyBusiness = connectableBusinesses[0];
              setCurrentBusiness(onlyBusiness);
            
              // Charger les informations employé si disponibles
              const empName = await loadEmployeeInfo(profile, onlyBusiness.id);
              setEmployeeName(empName);
              if (empName) {
                console.log('Employé connecté:', empName);
              }
            
              console.log('Authentification réussie pour business unique connectable:', onlyBusiness.id);
            
              // Optionnel : mettre à jour currentBusinessId dans Firestore si différent
              if (user && profile.currentBusinessId !== onlyBusiness.id) {
                try {
                  await updateDoc(doc(db, COLLECTIONS.users, user.uid), {
                    currentBusinessId: onlyBusiness.id,
                  });
                } catch (e) {
                  console.warn('Erreur mise à jour currentBusinessId pour business unique:', e);
                }
              }
            } else {
              // 🔹 Cas 3 : plusieurs business connectables, aucun en configuration
              // Restaurer le dernier établissement choisi si encore valide (évite de
              // renvoyer vers /business-selection à chaque remount, ex. clic notif push).
              const rememberedId = profile.currentBusinessId;
              const remembered = rememberedId
                ? connectableBusinesses.find((b) => b.id === rememberedId)
                : undefined;
              if (remembered) {
                console.log('Restauration du business mémorisé:', remembered.id);
                setCurrentBusiness(remembered);
                const empName = await loadEmployeeInfo(profile, remembered.id);
                setEmployeeName(empName);
              } else {
                console.log('Plusieurs business connectables disponibles, sélection requise');
                setCurrentBusiness(null);
                setEmployeeName(null);
              }
            }
            
            // Mettre à jour la dernière connexion
            await updateLastLogin();
          }
          
        } catch (profileError) {
          console.error('Erreur de profil/business:', profileError);
          
          // Messages d'erreur spécifiques
          let errorMsg = 'Erreur d\'authentification';
          if (profileError instanceof Error) {
            switch (profileError.message) {
              case 'UTILISATEUR_SANS_BUSINESS':
                errorMsg = 'Votre compte n\'est associé à aucun business. Contactez votre administrateur.';
                break;
              case 'AUCUN_BUSINESS_ACTIF':
                errorMsg = 'Aucun de vos business n\'est actuellement actif. Contactez votre administrateur.';
                break;
              case 'AUCUN_BUSINESS_CONNECTABLE':
                errorMsg = 'Tous vos business sont actuellement en cours de configuration. Merci de réessayer plus tard ou de contacter votre administrateur.';
                break;
              case 'BUSINESS_INTROUVABLE':
                errorMsg = 'Business associé introuvable. Contactez votre administrateur.';
                break;
              case 'BUSINESS_INACTIF':
                errorMsg = 'Votre business est actuellement inactif. Contactez votre administrateur.';
                break;
              case 'UTILISATEUR_NON_AUTORISE':
                errorMsg = 'Compte non autorisé. Contactez votre administrateur.';
                break;
            }
          }
          
          console.error(errorMsg);
          await signOut();
          return;
        }
        
      } else {
        console.log('Utilisateur déconnecté');
        setUser(null);
        setUserProfile(null);
        setCurrentBusiness(null);
        setEmployeeName(null);
        setAvailableBusinesses([]);
      }
      
    } catch (error) {
      console.error('Erreur critique lors de la gestion d\'état:', error);
      
      // En cas d'erreur critique, déconnecter complètement
      setUser(null);
      setUserProfile(null);
      setCurrentBusiness(null);
      setEmployeeName(null);
      setAvailableBusinesses([]);
      
      // Forcer la déconnexion si un utilisateur était connecté
      if (user) {
        await signOut();
      }
      
    } finally {
      setLoading(false);
    }
  });

  return () => {
    unsubscribe();
  };
}, []); // Pas de dépendances pour éviter les re-renders

  const value: AuthContextType = {
    user,
    employeeName,
    userProfile,
    currentBusiness,
    loading,
    signIn,
    signOut,
    updateLastLogin,
    isSuperAdmin,
    isCSM,
    switchToBusiness,
    switchToSuperAdmin,
    needsBusinessSelection,
    availableBusinesses,
    selectBusiness,
    switchBusiness,
    refreshCurrentBusiness,
    patchUserProfile,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};